import { ResponseWithPagination } from '@/types/api'
import { useInfiniteQuery } from '@tanstack/react-query'
import qs from 'query-string'
import { createApiRequest } from './createApiRequest'

export interface InfiniteDefinitionOptions {
    key: ((arg: any) => string[]) | string[]
    url: string
    server?: 'node' | 'python'
}

export interface InfiniteUsageOptions {
    queryParams?: Record<string, string | string[]>
    pathParams?: Record<string, string>
    enabled?: boolean
    pageSize?: number
}

export const createInfiniteQuery = <TItem>(definitionOptions: InfiniteDefinitionOptions) => {
    const useInfiniteQueryFn = (usageOptions?: InfiniteUsageOptions) => {
        const { url, key = [], server } = definitionOptions
        const { queryParams = {}, pathParams = {}, enabled = true, pageSize = 50 } = usageOptions || {}

        let finalUrl = url
        Object.entries(pathParams || {}).forEach(([param, value]) => {
            finalUrl = finalUrl.replace(`:${param}`, value)
        })

        const keyParams = queryParams ? qs.stringify(queryParams) : ''
        const pathParamsString = Object.entries(pathParams || {})
            .map(([k, v]) => `${k}=${v}`)
            .join('&')
        const allParams = [keyParams, pathParamsString].filter(Boolean).join('&')
        const queryKey = typeof key === 'function' ? key(allParams) : key

        return useInfiniteQuery<ResponseWithPagination<TItem>>({
            queryKey: [...queryKey, 'infinite'],
            queryFn: async ({ pageParam = 1 }) => {
                const paginatedParams = qs.stringify({
                    ...queryParams,
                    page: String(pageParam),
                    limit: pageSize.toString(),
                })
                const response = await createApiRequest<ResponseWithPagination<TItem>>({
                    url: finalUrl,
                    queryParams: paginatedParams,
                    method: 'GET',
                    server,
                })
                // NOTE:
                // `createApiRequest` already unwraps one level by returning `response.data`
                // (given our Axios interceptor returns the outer response body).
                // So at this point `response` should already be `{ data, pagination }`.
                return response
            },
            getNextPageParam: lastPage => {
                if (!lastPage?.pagination) return undefined
                const total = Number(lastPage.pagination.total)
                const limit = Number(lastPage.pagination.limit)
                const page = Number(lastPage.pagination.page)

                if (!Number.isFinite(total) || !Number.isFinite(limit) || !Number.isFinite(page) || limit <= 0) {
                    return undefined
                }

                const hasMore = page * limit < total
                return hasMore ? page + 1 : undefined
            },
            enabled,
        })
    }

    return useInfiniteQueryFn
}
